import React from 'react'
import { observer, inject } from 'mobx-react'
import DocumentTitle from 'react-document-title'
import PropTypes from 'prop-types'
import { formatMonthYear, formatQuarter } from '@atheneum/api/dist/lib'

// Widgets
import Performance from './Components/Performance'
import WebExtensions from './Components/WebExtensions'
import TeamRevenue from './Components/TeamRevenue'
import Bookmarks from './Components/Bookmarks'
import AccountRevenue from './Components/AccountRevenue'
import HonorariaAnalysis from './Components/HonorariaAnalysis'
import PrincipalTeams from './Components/PrincipalTeams'
import Warning from '@components/Icon/Warning'
import TempResponsiveScreen from '@components/TempResponsiveScreen'

class Dashboard extends TempResponsiveScreen {
  state = {
    activeTeamId: null,
    teamRevenueDate: formatMonthYear(),
    accountRevenueDate: formatMonthYear(),
    honorariaAnalysisDate: formatMonthYear(),
    performanceDate: formatMonthYear(),
    accountRevenueQuarter: formatQuarter(),
    accountRevenueYear: parseInt(new Date().getFullYear()),
    activeSubTeamNumber: 0,
    subTeamActive: false
  }

  static propTypes = {
    dashboardStore: PropTypes.object
  }

  setSubTeamActive = subTeamActive => {
    this.setState({ subTeamActive, activeSubTeamNumber: 0 })
  }

  setSubTeamActiveTab = activeSubTeamNumber => {
    this.setState({ activeSubTeamNumber, subTeamActive: true })
  }

  setPerformanceDate = performanceDate => {
    this.setState({ performanceDate: formatMonthYear(performanceDate) })
  }

  setTeamRevenueDate = teamRevenueDate => {
    this.setState({ teamRevenueDate: formatMonthYear(teamRevenueDate) })
  }

  setAccountRevenueDate = accountRevenueDate => {
    this.setState({ accountRevenueDate: formatMonthYear(accountRevenueDate) })
  }

  setHonorariaAnalysisDate = honorariaAnalysisDate => {
    this.setState({
      honorariaAnalysisDate: formatMonthYear(honorariaAnalysisDate)
    })
  }

  setActiveTeamId = activeTeamId => {
    this.setState({ activeTeamId })
    this.setSubTeamActive(false)
  }

  setAccountRevenueQuarter = accountRevenueQuarter => {
    this.setState({ accountRevenueQuarter })
  }

  setAccountRevenueYear = accountRevenueYear => {
    this.setState({ accountRevenueYear: parseInt(accountRevenueYear) })
  }

  prepareMetrics = metrics => {
    return [
      {
        id: 1,
        label: '1st delivery',
        value: metrics.deliveryFirst,
        tooltip:
          'Elapsed time in hours since 9:00 a.m. today until the moment your first Expert was submitted to Client'
      },
      {
        id: 2,
        label: 'Last delivery',
        value: metrics.deliveryLast,
        tooltip:
          'Elapsed time in hours since the moment your last Expert was submitted to Client'
      },
      {
        id: 3,
        label: 'Avg delivery',
        value: metrics.deliveryAverage,
        tooltip:
          'The average number of your delivered Experts (submitted to Client) per hour in this month'
      },
      {
        id: 4,
        label: '1st interview',
        value: metrics.deliveryFirst,
        tooltip: `Elapsed time in hours since 9:00 a.m. today until the moment your first Expert was marked to 'Interviewed'`
      },
      {
        id: 5,
        label: 'Last interview',
        value: metrics.interviewLast,
        tooltip: `Elapsed time in hours since the moment your last Expert was marked to 'Interviewed'`
      },
      {
        id: 6,
        label: 'Avg interview',
        value: metrics.interviewAverage,
        tooltip:
          'The average number of your Interviewed Experts per hour in this month'
      }
    ]
  }

  render() {
    if (process.env.REACT_APP_DASHBOARD_DISABLED) {
      return (
        <DocumentTitle title='Dashboard | Atheneum Partners'>
          <div className='dashboard-wrapper'>
            <div className='new-dashboard-notice'>
              <Warning className='widget__icon warning-icon' /> Sorry we're
              resolving some issues with the new dashboard - be back soon
            </div>
          </div>
        </DocumentTitle>
      )
    }
    const {
      performanceWidgetData,
      teamRevenueWidgetData,
      accountRevenueWidgetData,
      accountRevenueQuarterlyWidgetData,
      honorariaAnalysisWidgetData,
      extensionInfo,
      principalTeamsData,
      userStore
    } = this.props.dashboardStore

    return (
      <DocumentTitle title='Dashboard | Atheneum Partners'>
        <div className='dashboard-wrapper'>
          <h2 className='dashboard-header'>Overview</h2>
          <div className={'top-section'}>
            <div />
            {principalTeamsData.length > 0 && (
              <PrincipalTeams
                principalTeamsData={principalTeamsData}
                teamRevenueDate={this.state.teamRevenueDate}
                accountRevenueDate={this.state.accountRevenueDate}
                honorariaAnalysisDate={this.state.honorariaAnalysisDate}
                performanceDate={this.state.performanceDate}
                activeTeamId={this.state.activeTeamId}
                setActiveTeamId={this.setActiveTeamId}
                accountRevenueQuarter={this.state.accountRevenueQuarter}
                accountRevenueYear={this.state.accountRevenueYear}
              />
            )}
            <div />
          </div>
          <div className='dashboard-wrapper__widgets'>
            <div className='main-section'>
              <div className='dashboard-wrapper__bookmarks mobile'>
                <Bookmarks />
              </div>
              {teamRevenueWidgetData && (
                <div className='dashboard-wrapper__team-revenue'>
                  <TeamRevenue
                    date={this.state.teamRevenueDate}
                    setDate={this.setTeamRevenueDate}
                    activeTeamId={this.state.activeTeamId}
                    teamRevenueData={teamRevenueWidgetData}
                    setSubTeamActive={this.setSubTeamActive}
                    setSubTeamActiveTab={this.setSubTeamActiveTab}
                    activeSubTeamNumber={this.state.activeSubTeamNumber}
                    subTeamActive={this.state.subTeamActive}
                  />
                </div>
              )}
              {accountRevenueWidgetData && (
                <div className='dashboard-wrapper__account-revenue'>
                  <AccountRevenue
                    setQuarter={this.setAccountRevenueQuarter}
                    setYear={this.setAccountRevenueYear}
                    quarter={this.state.accountRevenueQuarter}
                    year={this.state.accountRevenueYear}
                    date={this.state.accountRevenueDate}
                    setDate={this.setAccountRevenueDate}
                    activeTeamId={this.state.activeTeamId}
                    accountRevenue={accountRevenueWidgetData}
                    accountRevenueQuarterly={accountRevenueQuarterlyWidgetData}
                  />
                </div>
              )}
              {honorariaAnalysisWidgetData && (
                <div className='dashboard-wrapper__honoraria-analysis'>
                  <HonorariaAnalysis
                    date={this.state.honorariaAnalysisDate}
                    setDate={this.setHonorariaAnalysisDate}
                    activeTeamId={this.state.activeTeamId}
                    honorariaAnalysis={honorariaAnalysisWidgetData}
                  />
                </div>
              )}
            </div>
            <div className='side-bar-section'>
              <div className='dashboard-wrapper__bookmarks desktop'>
                <Bookmarks />
              </div>
              {performanceWidgetData && (
                <div className='dashboard-wrapper__performance'>
                  <Performance
                    date={this.state.performanceDate}
                    setDate={this.setPerformanceDate}
                    performanceData={performanceWidgetData}
                  />
                </div>
              )}
              {extensionInfo && (
                <div className='dashboard-wrapper__web-extensions'>
                  <WebExtensions extensionInfo={extensionInfo} userStore={userStore}/>
                </div>
              )}
            </div>
          </div>
        </div>
      </DocumentTitle>
    )
  }
}

export default inject(
  'dashboardStore',
  'userStore',
  'growlStore'
)(observer(Dashboard))
